import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, Languages, ListChecks, Loader2, Plus, RotateCcw, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import {
  REPORT_LANGUAGES,
  ReportTranslationsApi,
  type ReportLanguage,
  type ReportTranslation,
  type ReportTranslationDefaults,
  type ReportTranslationInput,
} from '@/services/reports/reportTranslationsApi'
import { localeLabel, USER_LOCALES } from '@/data/userLocales'
import { formatDate } from '@/utils/format'
import { AppShell } from '../../components/AppShell'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { SearchInput } from '../../components/SearchInput'
import { SearchSelect } from '../../components/SearchSelect'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { CopyReportItemDialog } from '../../components/reports/CopyReportItemDialog'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

const LIST_PATH = '/reports/translations'

/** A label the network has reworded, rather than left blank or at the built-in wording. */
function isOverride(value: string | undefined, builtIn: string | undefined) {
  return !!value?.trim() && value.trim() !== (builtIn ?? '').trim()
}

// ── Inner form component ────────────────────────────────────────────────────

interface ReportTranslationEditFormProps {
  translation: ReportTranslation | null
  defaults: ReportTranslationDefaults
  id: string
  isNew: boolean
}

function ReportTranslationEditForm({
  translation,
  defaults,
  id,
  isNew,
}: ReportTranslationEditFormProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [labelSearch, setLabelSearch] = useState('')
  const [changedOnly, setChangedOnly] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)

  const form = useForm({
    defaultValues: {
      name: translation?.name ?? '',
      description: translation?.description ?? '',
      locale: translation?.locale ?? 'en',
      defaultTranslation: (translation?.defaultTranslation ?? 'EN') as ReportLanguage,
      translations: { ...(translation?.translations ?? {}) } as Record<string, string>,
    },
    onSubmit: ({ value }) => {
      const builtIn = defaults[value.defaultTranslation.toLowerCase()] ?? {}
      // Only what differs from the base language is kept; the rest follows it.
      const translations = Object.fromEntries(
        Object.entries(value.translations)
          .filter(([key, text]) => isOverride(text, builtIn[key]))
          .map(([key, text]) => [key, text.trim()])
      )
      mutation.mutate({
        name: value.name,
        description: value.description,
        locale: value.locale,
        defaultTranslation: value.defaultTranslation,
        translations,
        objectState: (translation?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) =>
      ReportTranslationsApi.update(id, { name: translation?.name ?? '', objectState }),
    ['report-translation', id],
    ['report-translations']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)
  const set = form.setFieldValue

  const builtIn = defaults[values.defaultTranslation.toLowerCase()] ?? {}
  // English names every label, whichever language the translation starts from.
  const labelNames = defaults.en ?? builtIn
  const keys = Object.keys(labelNames)
  const changedCount = keys.filter((key) => isOverride(values.translations[key], builtIn[key])).length
  const query = labelSearch.trim().toLowerCase()
  const shownKeys = keys.filter((key) => {
    if (changedOnly && !isOverride(values.translations[key], builtIn[key])) return false
    if (!query) return true
    return [labelNames[key], builtIn[key], values.translations[key]]
      .filter(Boolean)
      .some((text) => text!.toLowerCase().includes(query))
  })

  const mutation = useMutation({
    mutationFn: (input: ReportTranslationInput) =>
      isNew ? ReportTranslationsApi.create(id, input) : ReportTranslationsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['report-translation', id], data)
      queryClient.invalidateQueries({ queryKey: ['report-translations'] })
      markClean()
      if (isNew) navigate(`${LIST_PATH}/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew
          ? t('reportTranslations.createdSuccess')
          : t('reportTranslations.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('reportTranslations.createFailed')
            : t('reportTranslations.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('reportTranslations.title'), href: LIST_PATH },
    {
      label: isNew
        ? t('reportTranslations.newTranslation')
        : (translation?.name ?? t('reportTranslations.editTitle')),
    },
  ]

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.reportTranslation')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={translation?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.reportTranslation')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('reportTranslations.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="size-3.5" />
                {t('reportsModule.copy')}
              </Button>
              <ObjectStateToggle value={translation!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {translation && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={translation.id} /> },
              {
                label: t('reportTranslations.overrides'),
                content: String(Object.keys(translation.translations).length),
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(translation.createTime)}
                    updated={formatDate(translation.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Languages} />
              {t('reportTranslations.details')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid">
              <div className="p-field full">
                <FieldLabel>{t('common.description')}</FieldLabel>
                <form.Field name="description">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('reportTranslations.descriptionPlaceholder')}
                    />
                  )}
                </form.Field>
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('reportTranslations.localeHelp')}>
                  {t('reportTranslations.locale')}
                </FieldLabel>
                <SearchSelect
                  value={values.locale}
                  onChange={(v) => set('locale', v || 'en')}
                  options={USER_LOCALES.map((code) => ({
                    value: code,
                    label: localeLabel(code, i18n.language),
                  }))}
                  placeholder={localeLabel('en', i18n.language)}
                />
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('reportTranslations.baseLanguageHelp')}>
                  {t('reportTranslations.baseLanguage')}
                </FieldLabel>
                <RadioGroup
                  value={values.defaultTranslation}
                  onValueChange={(v) => set('defaultTranslation', v as ReportLanguage)}
                  className="flex flex-wrap gap-4 pt-1.5"
                >
                  {REPORT_LANGUAGES.map((language) => (
                    <Label key={language} className="font-normal">
                      <RadioGroupItem value={language} />
                      {t(`reportTranslations.languages.${language}`)}
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={ListChecks} />
              {t('reportTranslations.labels')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <p className="text-xs text-muted-foreground">
                {t('reportTranslations.labelsHelp', {
                  language: t(`reportTranslations.languages.${values.defaultTranslation}`),
                })}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <SearchInput value={labelSearch} onChange={setLabelSearch} />
                <div className="flex items-center gap-2">
                  <Switch id="labels-changed-only" checked={changedOnly} onCheckedChange={setChangedOnly} />
                  <Label htmlFor="labels-changed-only" className="font-normal">
                    {t('reportTranslations.changedOnly', { count: changedCount })}
                  </Label>
                </div>
              </div>
              {shownKeys.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('reportTranslations.noLabels')}</p>
              ) : (
                <div className="grid gap-x-4 gap-y-3 md:grid-cols-2">
                  {shownKeys.map((key) => {
                    const value = values.translations[key] ?? ''
                    const changed = isOverride(value, builtIn[key])
                    return (
                      <div key={key} className="p-field">
                        <FieldLabel htmlFor={`label-${key}`} hint={changed ? builtIn[key] : undefined}>
                          {labelNames[key]}
                        </FieldLabel>
                        <div className="flex items-center gap-1">
                          <Input
                            id={`label-${key}`}
                            value={value}
                            placeholder={builtIn[key]}
                            onChange={(e) => set(`translations.${key}`, e.target.value)}
                          />
                          {changed && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={t('reportTranslations.resetLabel')}
                              title={t('reportTranslations.resetLabel')}
                              onClick={() => set(`translations.${key}`, '')}
                            >
                              <RotateCcw className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(LIST_PATH)}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('reportTranslations.newTranslation') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      {translation && copyOpen && (
        <CopyReportItemDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          kind={t('objectTypes.reportTranslation')}
          source={translation}
          copy={(newId, name, description) =>
            ReportTranslationsApi.copy(translation, newId, name, description)
          }
          listKey="report-translations"
          pathFor={(newId) => `${LIST_PATH}/${newId}`}
        />
      )}
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function ReportTranslationEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: translation,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['report-translation', id],
    queryFn: () => ReportTranslationsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })
  // The form needs the built-in labels up front to tell overrides from defaults.
  const { data: defaults, isLoading: defaultsLoading } = useQuery({
    queryKey: ['report-translation-defaults'],
    queryFn: () => ReportTranslationsApi.getDefaults(),
    enabled: hasToken,
    staleTime: Infinity,
  })

  const crumbsLoading = [
    { label: t('reportTranslations.title'), href: LIST_PATH },
    {
      label: isNew ? t('reportTranslations.newTranslation') : t('reportTranslations.editTitle'),
    },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if ((isLoading && !isNew) || defaultsLoading) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <ReportTranslationEditForm
      key={id}
      translation={translation ?? null}
      defaults={defaults ?? {}}
      id={id!}
      isNew={isNew}
    />
  )
}
